/**
 * Game screen (UX 7.2, ART_SPEC 17.9). Two layouts over one component tree: the scene screen on
 * desktop and tablet, the pannable phone scene with a compact HUD and a bottom sheet on phones.
 * The keyboard map (7.7) and the live region (7.8) are attached here.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { artRegistryFor, useArtSets } from '../../assets/art/artRegistry';
import { useDebugBoot } from '../../debug/useDebugBoot';
import { useGame } from '../../store/gameStore';
import { Button } from '../common/Button';
import { GLYPH } from '../common/glyphs';
import { AiTicker } from '../game/AiTicker';
import { CommandPalette } from '../game/CommandPalette';
import { HoursStrip } from '../game/HoursStrip';
import { UndoButton } from '../game/UndoButton';
import { DeltaToast } from '../game/DeltaToast';
import { DebugPanel } from '../game/DebugPanel';
import { EventCards } from '../game/EventCards';
import { ConfirmModal } from '../game/ConfirmModal';
import { InfoModal } from '../game/InfoModal';
import { NewsFlash } from '../game/NewsFlash';
import { OutcomeModal } from '../game/OutcomeModal';
import { Hud } from '../game/Hud';
import { LocationPanel } from '../game/LocationPanel';
import { LogDrawer } from '../game/LogDrawer';
import { MenuSheet } from '../game/MenuSheet';
import { Standings } from '../game/Standings';
import { TravelSheet } from '../game/TravelSheet';
import { hours, turnOwner } from '../game/labels';
import { useIsPhone } from '../game/useIsPhone';
import { useKeyboard } from '../game/useKeyboard';
import { useSwipe } from '../game/useSwipe';
import { InteriorHeader } from '../scene/InteriorScene';
import { Newspaper, NewspaperButton } from '../scene/Newspaper';
import { PhoneScene } from '../scene/PhoneScene';
import { SceneGameScreen } from '../scene/SceneGameScreen';

function LiveRegion() {
  const { t } = useTranslation();
  const state = useGame((s) => s.state);
  const player = state?.players[state.activeSeat];
  if (!state || !player) return null;
  return (
    <p className="sr-only" aria-live="polite" data-testid="live">
      {`${t('live.turn', { owner: turnOwner(player.name, t), week: state.week })} · ${t(
        'live.hours',
        {
          n: hours(player.hoursLeft),
        },
      )} · ${t('live.money', { cash: player.cash })}`}
    </p>
  );
}

/**
 * Phone: slim status strip that stays on screen while the map and panels scroll under it, so
 * turn, week, hours and cash are never a scroll away (M11.6).
 */
function PhoneStatusBar() {
  const { t } = useTranslation();
  const state = useGame((s) => s.state);
  const togglePalette = useGame((s) => s.togglePalette);
  const toggleMenu = useGame((s) => s.toggleMenu);
  const player = state?.players[state.activeSeat];
  if (!state || !player) return null;
  return (
    <div
      className="sticky top-0 z-20 -mx-3 -mt-3 flex items-center gap-2 border-b border-line bg-surface px-3 py-2"
      data-testid="phone-status"
    >
      <h1 className="sr-only">{t('app.title')}</h1>
      <p className="min-w-0 grow text-sm font-semibold">
        {t('phone.status', {
          name: player.name,
          week: t('hud.week', { n: state.week }),
          hours: t('hud.hours', { n: hours(player.hoursLeft) }),
          cash: player.cash,
        })}
      </p>
      <UndoButton icon />
      <Button
        className="w-11 shrink-0 px-0"
        onClick={() => togglePalette()}
        aria-label={t('palette.heading')}
        data-testid="palette-btn"
      >
        {GLYPH.search}
      </Button>
      <Button className="shrink-0" onClick={toggleMenu} data-testid="menu-btn">
        {t('hud.menu')}
      </Button>
      <DeltaToast className="absolute right-3 top-full z-20 mt-1" />
      <div className="absolute inset-x-3 bottom-0.5">
        <HoursStrip compact />
      </div>
    </div>
  );
}

/** Phone bottom sheet: keeps the map visible and tappable above it. */
function PhoneSheet({ children }: { children: ReactNode }) {
  const closeTravel = useGame((s) => s.closeTravel);
  const swipe = useSwipe({ down: closeTravel });
  return (
    <div
      {...swipe}
      className="sheet-enter fixed inset-x-0 bottom-0 z-30 max-h-[60vh] overflow-y-auto rounded-t-xl border-t border-line bg-surface p-3 shadow-2xl"
      data-testid="phone-sheet"
    >
      {children}
    </div>
  );
}

export function GameScreen() {
  const { t } = useTranslation();
  // Re-render when an art pack is installed or switched (M9.12).
  useArtSets((s) => s.version);
  const state = useGame((s) => s.state);
  const pack = useGame((s) => s.pack);
  const travelOpen = useGame((s) => s.travelOpen);
  const logOpen = useGame((s) => s.logOpen);
  const standingsOpen = useGame((s) => s.standingsOpen);
  const menuOpen = useGame((s) => s.menuOpen);
  const phone = useIsPhone();
  const debug = useDebugBoot();
  const [paper, setPaper] = useState(false);
  useKeyboard();
  const active = state?.players[state.activeSeat];
  // Entering, leaving or a new turn changes what the page is about: start at the top of it (M11.6).
  useEffect(() => {
    if (phone) window.scrollTo(0, 0);
  }, [phone, active?.location, active?.inside, state?.activeSeat, state?.week]);
  if (!state) return null;
  // The illustrated scene (ART_SPEC 17.9) on desktop and tablet; phones get the pannable scene
  // with a list toggle (M9.9).
  if (!phone) {
    return (
      <>
        <LiveRegion />
        <EventCards />
        <OutcomeModal />
        <InfoModal />
        <ConfirmModal />
        <CommandPalette />
        <SceneGameScreen debug={debug} />
      </>
    );
  }
  // While a rival is playing, the panel and travel sheet would act on the AI's seat: show the
  // ticker instead (the keyboard map is gated the same way).
  const yourTurn = state.players[state.activeSeat]?.controller === 'human-local';

  const side = (
    <div className="flex flex-col gap-3">
      <Hud compact />
      {yourTurn && travelOpen && (
        <PhoneSheet>
          <TravelSheet />
        </PhoneSheet>
      )}
      {standingsOpen && <Standings />}
      {menuOpen && <MenuSheet />}
      <AiTicker />
      {yourTurn && pack && <InteriorHeader registry={artRegistryFor(pack)} />}
      {yourTurn && <LocationPanel />}
      {debug && <DebugPanel />}
      {logOpen && <LogDrawer />}
    </div>
  );

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-3 p-3">
      <LiveRegion />
      <EventCards />
      <OutcomeModal />
      <InfoModal />
      <ConfirmModal />
      <CommandPalette />
      <PhoneStatusBar />
      <div className="flex flex-col gap-3">
        <PhoneScene />
        <NewsFlash
          onRead={() => {
            setPaper(true);
          }}
        />
        <NewspaperButton onClick={() => setPaper((p) => !p)} />
        {paper && <Newspaper onClose={() => setPaper(false)} />}
        <h2 className="text-sm font-semibold text-ink-muted">{t('phone.actions')}</h2>
        {side}
      </div>
    </div>
  );
}
